import type { Objective, ObjectiveCategory, KeyResult, OkrCycle, Ppp } from "@/types";
import { ObjectiveStatus } from "@/types";
import { okrPct } from "./okrConstants";
import { dueStatusOf, localMidnight } from "./okr-dates";

const DAY_MS = 86_400_000;

/** Bucket key for KRs whose parent objective can't be resolved (BE hasn't shipped `objectivePid` yet). */
export const UNLINKED_KR_GROUP = "__unlinked__";

/** Selected cycle > the active cycle > the cycle spanning today > none. */
export function resolveCycle(cycles: OkrCycle[], selectedCyclePid: string, now = new Date()): OkrCycle | undefined {
  if (selectedCyclePid) return cycles.find((c) => c.pid === selectedCyclePid);
  const active = cycles.find((c) => c.status === "active");
  if (active) return active;
  const t = now.getTime();
  return cycles.find((c) => {
    const bounds = cycleBounds(c);
    return bounds !== null && t >= bounds.start && t < bounds.end;
  });
}

/** [start midnight, day-after-end midnight) in local time, so the whole end date counts as inside. */
function cycleBounds(cycle: OkrCycle): { start: number; end: number } | null {
  const start = localMidnight(cycle.startDate);
  const endDay = localMidnight(cycle.endDate);
  if (!start || !endDay) return null;
  const end = endDay.getTime() + DAY_MS;
  return end > start.getTime() ? { start: start.getTime(), end } : null;
}

export type PacingVerdict = "on-track" | "behind" | "ahead";

export interface CyclePacing {
  dayOfCycle: number;
  totalDays: number;
  elapsedPct: number;
  progressPct: number;
  verdict: PacingVerdict;
}

/** P = avg progress of ACTIVE objectives in `cycle`; verdict flips past a 10pt gap either way.
 * Sample: 90-day cycle, day 46 → elapsed 51%; 3 active objectives at 40/55/60 → progress 52% → "On track". */
export function computeCyclePacing(cycle: OkrCycle, objectivesInCycle: Objective[], now = new Date()): CyclePacing | null {
  const bounds = cycleBounds(cycle);
  if (!bounds) return null;
  const { start, end } = bounds;
  const totalDays = Math.round((end - start) / DAY_MS);
  const dayOfCycle = Math.min(totalDays, Math.max(1, Math.floor((now.getTime() - start) / DAY_MS) + 1));
  const elapsedPct = Math.min(100, Math.max(0, Math.round(((now.getTime() - start) / (end - start)) * 100)));
  const active = objectivesInCycle.filter((o) => o.status === ObjectiveStatus.ACTIVE);
  const progressPct = active.length
    ? Math.round(active.reduce((sum, o) => sum + okrPct(o.progress, o.targetProgress), 0) / active.length)
    : 0;
  const diff = progressPct - elapsedPct;
  const verdict: PacingVerdict = diff < -10 ? "behind" : diff > 10 ? "ahead" : "on-track";
  return { dayOfCycle, totalDays, elapsedPct, progressPct, verdict };
}

export interface CategoryRollup {
  category: ObjectiveCategory;
  count: number;
  avgProgress: number;
  overdueCount: number;
  dueSoonCount: number;
}

export function computeCategoryRollups(
  objectives: Objective[],
  categories: ObjectiveCategory[],
  now = new Date(),
): CategoryRollup[] {
  return categories.map((category) => {
    const items = objectives.filter((o) => o.category === category);
    const avgProgress = items.length
      ? Math.round(items.reduce((sum, o) => sum + okrPct(o.progress, o.targetProgress), 0) / items.length)
      : 0;
    let overdueCount = 0;
    let dueSoonCount = 0;
    for (const o of items) {
      const status = dueStatusOf(o.dueDate, o.status === ObjectiveStatus.ACTIVE, now);
      if (status === "overdue") overdueCount++;
      else if (status === "due-soon") dueSoonCount++;
    }
    return { category, count: items.length, avgProgress, overdueCount, dueSoonCount };
  });
}

/** `kr.objectivePid ?? kr.objective?.pid`; KRs with neither land in `UNLINKED_KR_GROUP`. */
export function groupKrsByObjective(keyResults: KeyResult[]): Map<string, KeyResult[]> {
  const map = new Map<string, KeyResult[]>();
  for (const kr of keyResults) {
    const key = kr.objectivePid ?? kr.objective?.pid ?? UNLINKED_KR_GROUP;
    const list = map.get(key);
    if (list) list.push(kr);
    else map.set(key, [kr]);
  }
  return map;
}

/** Per-KR: how many ranked PPPs link it, and the summed `deliverableCount` of those PPPs
 * (a deliverable linked to 2 PPPs is counted in both — see tooltip in OkrCoverageView). */
export function computeKrPppStats(ppps: Ppp[]): Map<string, { pppCount: number; deliverableCount: number }> {
  const map = new Map<string, { pppCount: number; deliverableCount: number }>();
  for (const ppp of ppps) {
    for (const kr of ppp.keyResults) {
      const entry = map.get(kr.pid) ?? { pppCount: 0, deliverableCount: 0 };
      entry.pppCount += 1;
      entry.deliverableCount += ppp.deliverableCount;
      map.set(kr.pid, entry);
    }
  }
  return map;
}

export interface KrCoverageRow {
  kr: KeyResult;
  pppCount: number;
  committedDeliverables: number;
}

export interface ObjectiveCoverageRow {
  objective: Objective;
  krs: KrCoverageRow[];
}

/** Sample: objective with 2 KRs, one linked to a PPP with deliverableCount=3, one linked to no PPP →
 * rows [{pppCount:1, committedDeliverables:3}, {pppCount:0, committedDeliverables:0}]. */
export function buildCoverageRows(
  objectives: Objective[],
  keyResults: KeyResult[],
  ppps: Ppp[],
): { rows: ObjectiveCoverageRow[]; unlinkedKrs: KeyResult[] } {
  const byObjective = groupKrsByObjective(keyResults);
  const pppStats = computeKrPppStats(ppps);
  const rows = objectives.map((objective) => ({
    objective,
    krs: (byObjective.get(objective.pid) ?? []).map((kr) => {
      const stats = pppStats.get(kr.pid) ?? { pppCount: 0, deliverableCount: 0 };
      return { kr, pppCount: stats.pppCount, committedDeliverables: stats.deliverableCount };
    }),
  }));
  return { rows, unlinkedKrs: byObjective.get(UNLINKED_KR_GROUP) ?? [] };
}

export const pppsWithoutKr = (ppps: Ppp[]): Ppp[] => ppps.filter((p) => p.keyResults.length === 0);

export const pppsWithNoDeliverable = (ppps: Ppp[]): Ppp[] => ppps.filter((p) => p.deliverableCount === 0);
